// Invite Service - Supabase based storage for publicly accessible invites
import { supabase } from './supabase';

// Check if Supabase is available
const checkSupabase = () => {
  if (!supabase) {
    console.error('Supabase client is not initialized. Check environment variables.');
    throw new Error('Database connection not available');
  }
};

// Generate a short unique ID for sharing
const generateShortId = () => {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// Create a new invite
export const createInvite = async (inviteData) => {
  checkSupabase();
  const shortId = generateShortId();

  const invite = {
    short_id: shortId,
    template_id: inviteData.templateId,
    question: inviteData.question,
    cta: inviteData.cta,
    pattern: inviteData.pattern,
    user_id: inviteData.userId || 'anonymous',
    user_email: inviteData.userEmail || 'anonymous',
    user_name: inviteData.userName || 'Someone special',
    created_at: new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from('invites')
    .insert([invite])
    .select()
    .single();

  if (error) {
    console.error('Error creating invite:', error);
    throw error;
  }

  // Return with camelCase for frontend compatibility
  return {
    ...data,
    shortId: data.short_id,
    templateId: data.template_id,
    userId: data.user_id,
    userEmail: data.user_email,
    userName: data.user_name,
    createdAt: data.created_at,
  };
};

// Get invite by short ID (public read)
export const getInviteByShortId = async (shortId) => {
  checkSupabase();
  const { data, error } = await supabase
    .from('invites')
    .select('*')
    .eq('short_id', shortId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      // No rows returned
      return null;
    }
    console.error('Error fetching invite:', error);
    throw error;
  }

  if (!data) return null;

  // Return with camelCase for frontend compatibility
  return {
    ...data,
    shortId: data.short_id,
    templateId: data.template_id,
    userId: data.user_id,
    userEmail: data.user_email,
    userName: data.user_name,
    createdAt: data.created_at,
    responses: data.responses || [],
  };
};

// Record a response to an invite
export const recordResponse = async (shortId, response) => {
  checkSupabase();
  // First get the current invite
  const { data: invite, error: fetchError } = await supabase
    .from('invites')
    .select('*')
    .eq('short_id', shortId)
    .single();

  if (fetchError) {
    console.error('Error fetching invite for response:', fetchError);
    throw fetchError;
  }

  // Add the new response
  const responses = invite.responses || [];
  responses.push({
    response,
    respondedAt: new Date().toISOString(),
  });

  // Update the invite
  const { data, error } = await supabase
    .from('invites')
    .update({ responses })
    .eq('short_id', shortId)
    .select()
    .single();

  if (error) {
    console.error('Error recording response:', error);
    throw error;
  }

  return data;
};

// Get all invites for a user
export const getUserInvites = async (userId) => {
  checkSupabase();
  const { data, error } = await supabase
    .from('invites')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching user invites:', error);
    throw error;
  }

  return data.map(invite => ({
    ...invite,
    shortId: invite.short_id,
    templateId: invite.template_id,
    userId: invite.user_id,
    userEmail: invite.user_email,
    userName: invite.user_name,
    createdAt: invite.created_at,
  }));
};
